import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * Chrome/Edge fire a `beforeinstallprompt` event when the PWA is installable.
 * We stash it and expose a subtle bottom banner the user can dismiss.
 * On iOS Safari there is no such event (users must use the Share sheet); we
 * skip the banner there to avoid noise.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && localStorage.getItem("notelosh_install_dismissed") === "1"
  );
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || dismissed || !deferred) return null;

  const install = async () => {
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "dismissed") {
        localStorage.setItem("notelosh_install_dismissed", "1");
        setDismissed(true);
      }
    } finally {
      setDeferred(null);
    }
  };

  const dismiss = () => {
    localStorage.setItem("notelosh_install_dismissed", "1");
    setDismissed(true);
    setDeferred(null);
  };

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2.5 rounded-full bg-[#121212] border border-[#27272A] shadow-lg text-sm text-[#F3F4F6]"
    >
      <span className="hidden sm:inline text-[#9CA3AF]">Install NoteLosh for a native feel</span>
      <button
        data-testid="install-prompt-btn"
        onClick={install}
        className="flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-xs font-medium transition-all"
      >
        <Download className="h-3.5 w-3.5" /> Install app
      </button>
      <button
        data-testid="install-prompt-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
        className="h-7 w-7 rounded-full hover:bg-[#1E1E1E] flex items-center justify-center text-[#6B7280] hover:text-[#F3F4F6]"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
